import {
  type Control,
  type FieldPath,
  type FieldValues,
  useWatch,
} from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FileDropzone } from "@/components/file-dropzone";
import { FormTooltip } from "@/components/ui/form-description";

export interface ConditionalRequiredConfig<
  TFieldValues extends FieldValues = FieldValues,
> {
  /** The field path to watch (e.g. "canProvidePhotos") */
  watchField: FieldPath<TFieldValues>;
  /** The value that makes this field required (e.g. "yes") */
  whenValue: string;
}

interface FileInputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  description?: string;
  descriptionAsTooltip?: boolean;
  label: string;
  name: TName;
  required?: boolean;
  requiredLabel?: string;
  accept?: Record<string, string[]>;
  multiple?: boolean;
  maxSize?: number;
  maxFiles?: number;
  disabled?: boolean;
  conditionalRequired?: ConditionalRequiredConfig<TFieldValues>;
}

export function FileInputField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  description,
  descriptionAsTooltip = false,
  label,
  name,
  required,
  requiredLabel = "Required",
  accept,
  multiple = false,
  maxSize,
  maxFiles,
  disabled = false,
  conditionalRequired,
  className,
  ...htmlProps
}: Readonly<FileInputFieldProps<TFieldValues, TName>>) {
  const watchedValue = useWatch({
    control,
    name: conditionalRequired?.watchField as FieldPath<TFieldValues>,
    disabled: !conditionalRequired,
  });

  const isRequired = conditionalRequired
    ? String(watchedValue) === conditionalRequired.whenValue
    : (required ?? false);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { value, onChange, ...field } }) => (
        <FormItem className={className} {...htmlProps}>
          <div>
            <FormLabel className="inline-block leading-snug">
              {label}
              {isRequired ? (
                <>
                  <span className="ms-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">({requiredLabel})</span>
                </>
              ) : null}
            </FormLabel>
            {description && !descriptionAsTooltip ? (
              <FormDescription>{description}</FormDescription>
            ) : null}
            {description && descriptionAsTooltip && (
              <FormTooltip description={description} />
            )}
          </div>
          <FormControl>
            <FileDropzone
              files={value || []}
              onFilesChange={onChange}
              accept={accept}
              multiple={multiple}
              maxSize={maxSize}
              maxFiles={maxFiles}
              disabled={disabled}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
